import { useEffect, useMemo, useState } from "react";
import axiosApi from "core/request";
import dayjs, { Dayjs } from "dayjs";
import { useAppSelector, useLocalStorage } from "hooks";
import moment from "moment/moment";
import { useTranslation } from "react-i18next";
import { sendNotification } from "ui/Toast";

import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { Button } from "@mui/material";

import Loader from "../../../../ui/Loader";
import { Text } from "../../../../ui/Text/Text";

import { SickCalendar } from "./components/SickCalendar";

import styles from "./styles.module.scss";

interface CalendarData {
  allDay: boolean | null;
  description: string | null;
  endDate: string;
  id: number | null;
  startDate: string | null;
  title: string | null;
  type: number | null;
  pureDays?: number;
}

export const SickLeave = () => {
  const { t, i18n } = useTranslation();
  const currentDate = new Date();

  const employeeId = useAppSelector((state) => state.auth.userId);

  const [isLoading, setIsLoading] = useState(false);
  const [holidaysToDelete, setHolidaysToDelete] = useState<number[] | string[]>(
    [],
  );
  const [calendarData, setCalendarData] = useState<CalendarData[]>([]);

  const [addedHolidays, setAddedHolidays] = useState<
    {
      id: string | null;
      date: Dayjs | null;
      dateEnd: Dayjs | null;
      note: string;
      pureDays?: number;
    }[]
  >([]);

  const [metaInfo, setMetaInfo] = useState({
    maxHolidaysPerYear: 0,
    isHolidaysModifiable: false,
    isVacationModifiable: false,
    maxVacationDaysPerYear: 28,
    vacationBalance: 0,
  });
  const [isEdit, setIsEdit] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [year, setYear] = useLocalStorage<number>(
    "vacationsSickDaysFilterYear",
    currentDate.getFullYear(),
  );

  const [updatedHolidays, setUpdatedHolidays] = useState<
    {
      id: number;
      dateEnd: Dayjs | null;
    }[]
  >([]);

  const handleUpdateExistingHoliday = (
    id: number,
    newDateEnd: Dayjs | null,
  ) => {
    setUpdatedHolidays((prev) => {
      const existing = prev.find((item) => item.id === id);
      if (existing) {
        return prev.map((item) =>
          item.id === id ? { ...item, dateEnd: newDateEnd } : item,
        );
      } else {
        return [...prev, { id, dateEnd: newDateEnd }];
      }
    });
  };

  const calculateFilledDatesFromPureDays = () => {
    return [...calendarData, ...addedHolidays].reduce((acc, item) => {
      const pureDays = item.pureDays ?? 0;
      return acc + pureDays;
    }, 0);
  };

  const sickLeaveCount = calculateFilledDatesFromPureDays();

  const yearToFetch = `${year}.01.01`;

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [calendarRes, metaRes] = await Promise.all([
        axiosApi.get("/personal-cabinet/calendar", {
          params: { employeeId, year, type: 2 },
        }),
        axiosApi.get(`/personal-cabinet/calendar/meta`, {
          params: { employeeId, date: yearToFetch },
        }),
      ]);

      setCalendarData(calendarRes.data);
      setMetaInfo(metaRes.data);
    } catch (error) {
      console.error("Error fetching sick leave data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeYear = (year: number | undefined): void => {
    if (year === undefined) {
      return;
    }
    setYear(year);
  };

  useEffect(() => {
    fetchData();
  }, [employeeId, year]);

  const handleSave = async () => {
    try {
      setIsLoading(true);

      const daysOffDtoNew = addedHolidays.map((holiday) => {
        const localStartDate = dayjs(holiday.date).startOf("day");
        const localEndDate = dayjs(holiday.dateEnd).startOf("day");
        const offset = localStartDate.utcOffset();
        const offsetInHours = dayjs().utcOffset() / 60;

        const adjustedDate = localStartDate
          .utc()
          .add(offset + offsetInHours, "minute")
          .format();

        const adjustedEndDate = localEndDate
          .utc()
          .add(offset + offsetInHours, "minute")
          .format();

        return {
          startDate: adjustedDate,
          finishDate: adjustedEndDate,
          sign: 2,
          remark: holiday.note || null,
          pureDays: holiday.pureDays,
        };
      });

      const daysOffDtoUpdated = updatedHolidays.map((holiday) => {
        const localEndDate = dayjs(holiday.dateEnd).startOf("day");
        const offset = localEndDate.utcOffset();
        const offsetInHours = dayjs().utcOffset() / 60;
        const adjustedEndDate = localEndDate
          .utc()
          .add(offset + offsetInHours, "minute")
          .format();

        const currentDate = calendarData.find((item) => item.id === holiday.id);
        return {
          ...currentDate,
          id: holiday.id,
          finishDate: adjustedEndDate,
          sign: 2,
        };
      });

      if (holidaysToDelete.length > 0) {
        try {
          await axiosApi.delete("/personal-cabinet/calendar/daysoff", {
            data: { ids: holidaysToDelete },
            headers: {
              "Accept-Language": i18n.language,
            },
          });
        } catch (error: any) {
          const message =
            error?.response?.data?.errors?.[""]?.[0] ||
            t("notifications.errorDelete");
          sendNotification(message, "error");
        }
      }

      if (daysOffDtoNew.length > 0) {
        try {
          const payloadNew = {
            employeeId,
            daysOffDto: daysOffDtoNew,
          };
          await axiosApi.post(
            "/personal-cabinet/calendar/daysoff",
            payloadNew,
            {
              headers: {
                "Accept-Language": i18n.language,
              },
            }
          );
        } catch (error: any) {
          const errors = error?.response?.data?.errors;
          const firstError =
            errors && typeof errors === "object"
              ? errors[Object.keys(errors)[0]]?.[0]
              : null;

          const message = firstError || t("notifications.errorCreate");
          sendNotification(message, "error");
        }
      }

      const toUpdate = daysOffDtoUpdated.filter(
        (updateItem) => !holidaysToDelete.includes(updateItem.id as never)
      );

      if (toUpdate.length > 0) {
        for (const item of toUpdate) {
          if (item?.id != null) {
            const pureDays =
              dayjs(item.finishDate).diff(item.startDate, "day") + 1;
            try {
              await axiosApi.patch(
                `/personal-cabinet/calendar/daysoff`,
                {
                  employeeId,
                  daysOffId: item.id,
                  finishDate: item.finishDate,
                  pureDays,
                },
                {
                  headers: {
                    "Accept-Language": i18n.language,
                  },
                }
              );
            } catch (error: any) {
              const message =
                error?.response?.data?.errors?.[""]?.[0] ||
                t("notifications.errorUpdate");
              sendNotification(message, "error");
            }
          }
        }
      }

      fetchData();

      sendNotification(t("notifications.successfullySaved"), "success");

      setAddedHolidays([]);
      setUpdatedHolidays([]);
      setHolidaysToDelete([]);
      setIsEdit(false);
    } catch (error) {
      sendNotification(t("statuses.Error"), "error");
      console.error("Error saving sick leave:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNewHoliday = (holiday: {
    date: Dayjs | null;
    dateEnd: Dayjs | null;
    note: string;
    id: string | null;
    pureDays?: number;
  }) => {
    setAddedHolidays((prev) => [...prev, holiday]);
  };

  const handleMarkForDelete = (id: number | string) => {
    if (!calendarData.find((item) => item.id === id)) return;
    // @ts-ignore
    setHolidaysToDelete((prev) => [...prev, id]);
    setCalendarData((prev) => prev.filter((item) => item.id !== id));
    setAddedHolidays((prev) => prev.filter((holiday) => holiday.id !== id));
  };

  const handleRemoveAddedHoliday = (id: string | null) => {
    setAddedHolidays((prev) => prev.filter((holiday) => holiday.id !== id));
  };

  const toggleRowExpansion = (id: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <div>
      {!isLoading && (
        <div className={styles.meta_info}>
          <div>
            <div className={styles.box_year}>
              <ArrowLeftIcon
                htmlColor="gray"
                sx={{ color: "green", fontSize: "40px" }}
                onClick={() => handleChangeYear(year - 1)}
              />
              <p>{year}</p>
              <ArrowRightIcon
                htmlColor="gray"
                sx={{
                  color: "green",
                  fontSize: "40px",
                }}
                onClick={() => handleChangeYear(year + 1)}
              />
            </div>
          </div>
          <div style={{ marginLeft: "50px" }} className={styles.meta_info}>
            {dayjs(currentDate).format("YYYY") === String(year) && !isEdit && (
              <Button
                disabled={isLoading}
                variant="contained"
                onClick={isEdit ? handleSave : () => setIsEdit(true)}
              >
                {t("buttons.Edit")}
              </Button>
            )}

            {isEdit && (
              <>
                <Button
                  disabled={isLoading}
                  variant="contained"
                  onClick={isEdit ? handleSave : () => setIsEdit(true)}
                >
                  {t("buttons.Save")}
                </Button>
                <Button
                  disabled={isLoading}
                  variant="contained"
                  onClick={() => {
                    setIsEdit(false);
                    setAddedHolidays([]);
                    setHolidaysToDelete([]);
                    fetchData();
                  }}
                >
                  {t("buttons.Close")}
                </Button>
              </>
            )}
          </div>
        </div>
      )}
      <div className={styles.cards_container}>
        {isLoading ? (
          <div style={{ width: "100%" }}>
            <Loader />
          </div>
        ) : isEdit ? (
          <>
            <SickCalendar isNew onAdd={handleAddNewHoliday} />
            {[...calendarData, ...addedHolidays].map((item: any) => (
              <SickCalendar
                handleUpdateExistingHoliday={handleUpdateExistingHoliday}
                dateEnd={
                  typeof item.id === "number" ? item.endDate : item.dateEnd
                }
                key={item.id || item.id}
                id={item.id || null}
                date={typeof item.id === "string" ? item.date : item.startDate}
                note={
                  typeof item.id === "string" ? item.note : item.description
                }
                onDelete={
                  typeof item.id === "number"
                    ? () => handleMarkForDelete(item.id!)
                    : () => handleRemoveAddedHoliday(item.id)
                }
              />
            ))}
          </>
        ) : (
          <div>
            <div className={styles.summary_table}>
              <table className={styles.summary_table_content}>
                <tbody>
                  <tr>
                    <th style={{ backgroundColor: "white" }}>
                      {t("vacationsSickDays.totalSickDaysPerYear")}
                    </th>
                    <td>
                      <span className={styles.value_color}>
                        {sickLeaveCount}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th style={{ backgroundColor: "white" }}>
                      {t("vacationsSickDays.paidSickLeave")}
                    </th>
                    <td>
                      <span className={styles.value_color}>
                        {sickLeaveCount <= 5 ? sickLeaveCount : 5}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className={styles.sick_leave_table}>
              <table className={styles.sick_leave_table_content}>
                <thead className={styles.table_header}>
                  {/* <tr className={styles.table_header}> */}
                  <tr>
                    <th style={{ backgroundColor: "white" }}>
                      {t("vacationsSickDays.dates")}
                    </th>
                    <th style={{ backgroundColor: "white" }}>
                      {t("vacationsSickDays.daysCount")}
                    </th>
                    <th style={{ backgroundColor: "white" }}>
                      {t("vacationsSickDays.note")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {calendarData.map((item: CalendarData, idx) => {
                    const startDate = moment(item.startDate);
                    const endDate = moment(item.endDate);
                    const daysDifference = endDate.diff(startDate, "days") + 1;
                    const isExpanded = expandedRows.has(item.id!);
                    const description =
                      item.description ||
                      String(t("vacationsSickDays.noReason"));
                    const isLongText = description.length > 50;

                    return (
                      <tr key={item.id}>
                        <td>
                          {startDate.format("DD.MM.YYYY")} -{" "}
                          {endDate.format("DD.MM.YYYY")}
                        </td>
                        <td>{daysDifference}</td>
                        <td className={isExpanded ? styles.expanded_cell : ""}>
                          {isExpanded ? (
                            <div>
                              <div className={styles.full_text}>
                                {description}
                              </div>
                              <button
                                className={styles.collapse_button}
                                onClick={() => toggleRowExpansion(item.id!)}
                              >
                                Свернуть
                              </button>
                            </div>
                          ) : (
                            <div>
                              <div className={styles.truncated_text}>
                                {description.length > 50
                                  ? `${description.substring(0, 50)}...`
                                  : description}
                              </div>
                              {isLongText && (
                                <button
                                  className={styles.expand_button}
                                  onClick={() => toggleRowExpansion(item.id!)}
                                >
                                  Смотреть полностью
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {calendarData.length === 0 && (
                    <tr>
                      <td colSpan={3} className={styles.no_data}>
                        {t("vacationsSickDays.noSickLeaveData")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
